import React, { useMemo, useState } from 'react';
import { Network, Search, Building2, Home, Plus } from 'lucide-react';
import AppHeader from '../components/layout/AppHeader';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import ErrorState from '../components/shared/ErrorState';
import EmptyState from '../components/shared/EmptyState';
import EntityLink from '../components/shared/EntityLink';
import RoleChips from '../components/relationships/RoleChips';
import RelationshipCurateModal from '../components/relationships/RelationshipCurateModal';
import { useContactRelationships } from '../hooks/useContactRelationships';
import { useRoleLabels } from '../hooks/useRoleLabels';

/**
 * G5E — Relationship Hub.
 * Lista contatos de v_contato_relationship_context (busca por nome/e-mail) com as
 * empresas representadas (nome + papéis) e imóveis (nome, matrícula, papéis).
 * Curadoria de vínculos via RPC (RelationshipCurateModal).
 */

function fullName(r) {
  return [r.first_name, r.last_name].filter(Boolean).join(' ').trim() || r.email || '(sem nome)';
}

function ContactRelationshipRow({ row, name, roleLabel, onCurate }) {
  const empresas = Array.isArray(row.represented_empresas) ? row.represented_empresas : [];
  const imoveis = Array.isArray(row.represented_imoveis) ? row.represented_imoveis : [];

  return (
    <div className="bg-white border border-slate-200 rounded p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <EntityLink entityType="contato" entityId={row.contato_id} label={name} />
          {row.email && <div className="text-[11px] text-slate-500 truncate">{row.email}</div>}
        </div>
        <button
          onClick={onCurate}
          className="px-2 py-1 text-[11px] bg-slate-100 hover:bg-blue-50 text-slate-700 hover:text-blue-700 rounded flex items-center gap-1 flex-shrink-0"
        >
          <Plus size={12} /> Curar
        </button>
      </div>

      <div className="mt-2 grid md:grid-cols-2 gap-3">
        {/* Empresas representadas */}
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1 flex items-center gap-1">
            <Building2 size={11} /> Empresas
          </div>
          {empresas.length === 0 ? (
            <div className="text-[11px] text-slate-400">—</div>
          ) : (
            <div className="space-y-1">
              {empresas.map((e) => (
                <div key={e.empresa_id} className="flex items-center gap-1.5 flex-wrap text-xs">
                  <EntityLink entityType="empresa" entityId={e.empresa_id} label={e.name} showIcon={false} />
                  <RoleChips roles={e.roles} roleLabel={roleLabel} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Imóveis representados */}
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1 flex items-center gap-1">
            <Home size={11} /> Imóveis
          </div>
          {imoveis.length === 0 ? (
            <div className="text-[11px] text-slate-400">—</div>
          ) : (
            <div className="space-y-1">
              {imoveis.map((im) => (
                <div key={im.imovel_id} className="flex items-center gap-1.5 flex-wrap text-xs">
                  <EntityLink entityType="imovel" entityId={im.imovel_id} label={im.nome} showIcon={false} />
                  {im.matricula && <span className="text-[10px] text-slate-500">mat. {im.matricula}</span>}
                  <RoleChips roles={im.roles} roleLabel={roleLabel} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function RelationshipsPage() {
  const { data, loading, error, refetch } = useContactRelationships();
  const { roleLabel } = useRoleLabels();
  const [search, setSearch] = useState('');
  // null = fechado | 'new' = novo vínculo livre | { id, label } = curar contato específico
  const [curateTarget, setCurateTarget] = useState(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((r) => `${fullName(r)} ${r.email || ''}`.toLowerCase().includes(q));
  }, [data, search]);

  return (
    <>
      <AppHeader title="Relacionamentos" subtitle={`${filtered.length} de ${data.length}`} icon={Network} />
      <div className="p-4 lg:p-6 space-y-3">
        <div className="bg-white border border-slate-200 rounded p-3 flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar contato por nome ou e-mail..."
              className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded text-xs focus:outline-none focus:border-blue-400"
            />
          </div>
          <button
            onClick={() => setCurateTarget('new')}
            className="px-3 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1 flex-shrink-0"
          >
            <Plus size={13} /> Curar relacionamento
          </button>
        </div>

        {loading && <LoadingSpinner label="Carregando relacionamentos..." />}
        {!loading && error && <ErrorState message={error} onRetry={refetch} />}
        {!loading && !error && filtered.length === 0 && (
          <EmptyState
            icon={Network}
            message={data.length === 0 ? 'Nenhum relacionamento curado ainda.' : 'Nenhum contato corresponde à busca.'}
            hint={data.length === 0 ? 'Use "Curar relacionamento" para registrar o primeiro vínculo.' : undefined}
          />
        )}
        {!loading && !error && filtered.length > 0 && (
          <div className="space-y-2">
            {filtered.map((r) => (
              <ContactRelationshipRow
                key={r.contato_id}
                row={r}
                name={fullName(r)}
                roleLabel={roleLabel}
                onCurate={() => setCurateTarget({ id: r.contato_id, label: fullName(r) })}
              />
            ))}
          </div>
        )}
      </div>

      {curateTarget && (
        <RelationshipCurateModal
          defaultContato={curateTarget === 'new' ? null : curateTarget}
          onSaved={refetch}
          onClose={() => setCurateTarget(null)}
        />
      )}
    </>
  );
}
